import { IAttributeLayout, AttributeSize } from "../core/types/webgl_types.js";
import { ShaderChunks } from "./shader_chunks.js";
import { generateAttributeDeclarations } from "./shader_compiler.js";
import { vertexShaderTemplate, fragmentShaderTemplate } from "./shader_blueprint.js";

export class ShaderBuilder
{
  private primitive_type : GLenum             = WebGL2RenderingContext.TRIANGLES;
  private attributes     : IAttributeLayout[] = [];
  private uniforms       : Set<string>        = new Set();
  private defines        : Set<string>        = new Set();
  private varyings       : { type: string, name: string, modifier: string }[] = [];
  private chunks         : Set<string>        = new Set();
  private vertex_logic   : string             = '';
  private fragment_logic : string             = '';
  private raw_decls      : string[]           = [];
  
  private raw_vertex_shader   : string | null = null;
  private raw_fragment_shader : string | null = null;

  public withPrimitive( primitive: GLenum ): this
  {
    this.primitive_type = primitive;
    return this;
  }

  public withAttributes( layout: IAttributeLayout[] ): this
  {
    this.attributes = layout;
    return this;
  }
  
  public withUniform( type: string, name: string ): this
  {
    this.uniforms.add( `uniform ${type} ${name};` );
    return this;
  }

  public withVarying( type: string, name: string, modifier: string = '' ): this
  {
    this.varyings.push( { type, name, modifier } );
    return this;
  }

  public includeChunk( chunk_name: keyof typeof ShaderChunks ): this
  {
    this.chunks.add( chunk_name as string );
    return this;
  }
  
  public withDefine( macro: string ): this
  {
    this.defines.add( macro );
    return this;
  }

  public withRawDeclaration( decl: string ): this
  {
    this.raw_decls.push( decl );
    return this;
  }

  public withVertexLogic( logic: string ): this
  {
    this.vertex_logic = logic;
    return this;
  }

  public withFragmentLogic( logic: string ): this
  {
    this.fragment_logic = logic;
    return this;
  }

  public overrideVertexShader( raw_glsl: string ): this
  {
    this.raw_vertex_shader = raw_glsl;
    return this;
  }

  public overrideFragmentShader( raw_glsl: string ): this
  {
    this.raw_fragment_shader = raw_glsl;
    return this;
  }

  private resolveChunks(): string
  {
    let chunk_code = '';
    this.chunks.forEach( chunk => {
      if ( ShaderChunks[chunk] )
      {
        chunk_code += ShaderChunks[chunk] + '\n';
      }
    } );
    return chunk_code;
  }

  public build(): { vertex_src: string; fragment_src: string }
  {
    if ( this.raw_vertex_shader && this.raw_fragment_shader )
    {
      return {
        vertex_src   : this.raw_vertex_shader,
        fragment_src : this.raw_fragment_shader
      };
    }

    if ( this.primitive_type === WebGL2RenderingContext.TRIANGLES )
    {
      this.chunks.add( 'getCornerFromIndex' );
      this.chunks.add( 'getVertexFromIndex' );
    }

    let auto_logic: string = '';
    
    // Auto-wiring boilerplate
    for ( const attr of this.attributes )
    {
      if ( attr.name === 'a_position' ) auto_logic += '      position = a_position;\n';
      else if ( attr.name === 'a_size' ) 
      {
        if ( this.primitive_type === WebGL2RenderingContext.TRIANGLES )
        {
          if ( attr.size === AttributeSize.Float ) auto_logic += '      size = vec2( a_size );\n';
          else auto_logic += '      size = a_size;\n';
        }
        else
        {
          if ( attr.size === AttributeSize.Float ) auto_logic += '      size = a_size;\n';
        }
      }
      else if ( attr.name === 'a_alpha' )
      {
        auto_logic += '      v_alpha = a_alpha;\n';
        if ( !this.varyings.find( v => v.name === 'v_alpha' ) )
          this.withVarying( 'float', 'v_alpha' );
      }
      else if ( attr.name === 'a_brightness' )
      {
        auto_logic += '      v_brightness = a_brightness;\n';
        if ( !this.varyings.find( v => v.name === 'v_brightness' ) )
          this.withVarying( 'float', 'v_brightness' );
      }
      else if ( attr.name === 'a_uv' && this.primitive_type === WebGL2RenderingContext.TRIANGLES )
      {
        auto_logic += '      v_uv = mix( a_uv.xy, a_uv.zw, uv );\n';
        if ( !this.varyings.find( v => v.name === 'v_uv' ) )
          this.withVarying( 'vec2', 'v_uv' );
      }
    }

    const final_vertex_logic: string = auto_logic + '\n' + this.vertex_logic;

    const attr_decl    = generateAttributeDeclarations( this.attributes );
    const varyings_out = this.varyings.map( v => `${v.modifier ? v.modifier + ' ' : ''}out ${v.type} ${v.name};` ).join( '\n' );
    const varyings_in  = this.varyings.map( v => `${v.modifier ? v.modifier + ' ' : ''}in ${v.type} ${v.name};` ).join( '\n' );
    const defines_str  = Array.from( this.defines ).join( '\n' );
    const uniforms_str = Array.from( this.uniforms ).join( '\n' );
    const chunks_str   = this.resolveChunks();
    const decls_str    = this.raw_decls.join( '\n' );

    const vertex_src = vertexShaderTemplate(
      this.primitive_type,
      attr_decl,
      defines_str + '\n' + uniforms_str,
      varyings_out,
      chunks_str + '\n' + decls_str,
      final_vertex_logic
    );

    const fragment_src = fragmentShaderTemplate(
      defines_str + '\n' + uniforms_str,
      varyings_in,
      chunks_str + '\n' + decls_str,
      this.fragment_logic
    );

    return { vertex_src, fragment_src };
  }
}
